// Three-way merge used when two devices change the data at the same time.
//   base:   the last version this device and the server agreed on
//   mine:   this device's version (base + changes made here)
//   theirs: the server's version (base + changes made elsewhere)
// Objects merge key by key, lists of things with an id merge item by item, so different edits on different devices
// are all kept. When both sides changed the very same value, this device's change wins (it's the one the person is
// looking at right now).

export const same = (a, b) => a === b || JSON.stringify(a) === JSON.stringify(b);
const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isIdList = (v) => Array.isArray(v) && v.every((x) => isObj(x) && typeof x.id === 'string');
const MISSING = undefined;

function mergeLists(base, mine, theirs) {
  const b = new Map((base || []).map((x) => [x.id, x]));
  const m = new Map(mine.map((x) => [x.id, x]));
  const t = new Map(theirs.map((x) => [x.id, x]));
  const pick = (id) => merge3(b.get(id), m.get(id), t.get(id));
  // Keep this side's order if it reordered anything, else theirs; slot in items only the other side has
  const ids = (xs) => xs.map((x) => x.id);
  const reordered = !same(ids(mine).filter((id) => b.has(id)), ids(base || []).filter((id) => m.has(id)));
  const [primary, secondary] = reordered ? [mine, theirs] : [theirs, mine];
  const order = ids(primary);
  const placed = new Set(order);
  let prev = null;
  for (const id of ids(secondary)) {
    if (!placed.has(id)) {
      order.splice(prev === null ? 0 : order.indexOf(prev) + 1, 0, id);
      placed.add(id);
    }
    prev = id;
  }
  // Something deleted on one side and untouched on the other stays deleted
  return order.map(pick).filter((x) => x !== MISSING);
}

export function merge3(base, mine, theirs) {
  if (same(mine, theirs)) return mine;
  if (same(mine, base)) return theirs;
  if (same(theirs, base)) return mine;
  // Both changed it
  if (isObj(mine) && isObj(theirs)) {
    const b = isObj(base) ? base : {};
    const out = {};
    for (const k of new Set([...Object.keys(mine), ...Object.keys(theirs)])) {
      const v = merge3(b[k], mine[k], theirs[k]);
      if (v !== MISSING) out[k] = v;
    }
    return out;
  }
  if (isIdList(mine) && isIdList(theirs) && (base === MISSING || isIdList(base))) return mergeLists(base, mine, theirs);
  // Deleted here but changed there (or the other way round): keep the edit
  if (mine === MISSING) return theirs;
  if (theirs === MISSING) return mine;
  return mine;
}
